import React from "react";
import {
  AccountBadge,
  mapSupabaseToSyncStatus,
} from "../../components/AccountBadge";
import { actionColors } from "../../themeColorTokens";

// ─── SVG shape presets ────────────────────────────────────────────────────────

function makeSvgDataUrl(innerSvg: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ` +
    `stroke="rgba(255,255,255,0.82)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
    `${innerSvg}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const PRESET_SHAPES = [
  { id: "circle",   label: "Circle",   url: makeSvgDataUrl('<circle cx="12" cy="12" r="8"/>') },
  { id: "diamond",  label: "Diamond",  url: makeSvgDataUrl('<path d="M12 4L20 12L12 20L4 12Z"/>') },
  { id: "triangle", label: "Triangle", url: makeSvgDataUrl('<polygon points="12,4 21,20 3,20"/>') },
  { id: "hexagon",  label: "Hexagon",  url: makeSvgDataUrl('<polygon points="12,3 20.2,7.5 20.2,16.5 12,21 3.8,16.5 3.8,7.5"/>') },
  { id: "square",   label: "Square",   url: makeSvgDataUrl('<rect x="4" y="4" width="16" height="16" rx="2"/>') },
  { id: "cross",    label: "Cross",    url: makeSvgDataUrl('<path d="M5 5L19 19M19 5L5 19"/>') },
  { id: "plus",     label: "Plus",     url: makeSvgDataUrl('<path d="M12 4v16M4 12h16"/>') },
  { id: "rings",    label: "Rings",    url: makeSvgDataUrl('<circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="9"/>') },
];

type AvatarChooserMode = "preset" | "upload" | null;

const isUploadedPhoto = (key: string) =>
  /^data:image\/(png|jpeg|jpg|gif|webp|bmp)/.test(key);

// ─── Avatar mode tab ──────────────────────────────────────────────────────────

function ModeTab({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        border: active ? "1px solid rgba(59,130,246,0.6)" : "1px solid rgba(255,255,255,0.1)",
        borderRadius: 8,
        background: active ? "rgba(59,130,246,0.1)" : "rgba(255,255,255,0.03)",
        color: active ? "#7dd3fc" : "rgba(255,255,255,0.55)",
        fontSize: "0.82rem",
        fontWeight: active ? 700 : 500,
        padding: "0.38rem 0.75rem",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 100ms ease",
      }}
    >
      {label}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const btn: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 10,
  background: "rgba(255,255,255,0.05)",
  color: "white",
  fontSize: "0.88rem",
  padding: "0.58rem 0.9rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  cursor: "pointer",
  fontWeight: 700,
};

const sectionStyle: React.CSSProperties = {
  border: "1px solid rgba(148,163,184,0.14)",
  borderRadius: 12,
  padding: "1rem 1.05rem",
  background: "rgba(255,255,255,0.015)",
  width: "100%",
  boxSizing: "border-box",
};

type Props = {
  displayName: string;
  avatarKey: string;
  onSaveDisplayNameAndAvatar: (name: string, key: string) => Promise<void>;
  supabaseUserId: string | null;
  supabaseUserEmail: string | null;
  supabaseStatus: string;
  supabaseError: string | null;
  isDemoMode: boolean;
};

export function AccountSection({
  displayName,
  avatarKey,
  onSaveDisplayNameAndAvatar,
  supabaseUserId,
  supabaseUserEmail,
  supabaseStatus,
  supabaseError,
  isDemoMode,
}: Props) {
  const disableWrites = isDemoMode;
  const isSignedIn = !!supabaseUserId;
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  const [draftName, setDraftName] = React.useState(displayName);
  const [draftAvatarKey, setDraftAvatarKey] = React.useState(avatarKey);
  const [avatarChooserMode, setAvatarChooserMode] = React.useState<AvatarChooserMode>(null);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [saveBusy, setSaveBusy] = React.useState(false);

  React.useEffect(() => { setDraftName(displayName); }, [displayName]);
  React.useEffect(() => {
    setDraftAvatarKey(avatarKey);
    setAvatarChooserMode(null);
  }, [avatarKey]);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const isDirty = draftName !== displayName || draftAvatarKey !== avatarKey;

  const handleSave = async () => {
    if (disableWrites) return;
    setSaveBusy(true);
    try {
      await onSaveDisplayNameAndAvatar(draftName, draftAvatarKey);
      setAvatarChooserMode(null);
      setSaveMsg("Saved!");
      setTimeout(() => setSaveMsg(null), 2200);
    } catch {
      setSaveMsg("Save failed — try again.");
    } finally {
      setSaveBusy(false);
    }
  };

  const handleAvatarUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === "string") setDraftAvatarKey(result);
    };
    reader.readAsDataURL(file);
  };

  const toggleChooserMode = (mode: Exclude<AvatarChooserMode, null>) => {
    setAvatarChooserMode((current) => (current === mode ? null : mode));
  };

  const resolvedSyncStatus = mapSupabaseToSyncStatus(isSignedIn, supabaseStatus, supabaseError);

  // Resolved preview name mirrors AppShell logic
  const previewName = draftName.trim() ||
    (isSignedIn ? (supabaseUserEmail?.split("@")[0] ?? "") : "") ||
    (isSignedIn ? "Account" : "Guest");

  const previewInitials = React.useMemo(() => {
    const split = previewName.trim().split(/\s+/).filter(Boolean);
    if (!split.length) return "A";
    return split.slice(0, 2).map((v) => v[0]?.toUpperCase() ?? "").join("") || "A";
  }, [previewName]);
  const defaultInitial = previewInitials[0] || "A";
  const isPresetSelected = PRESET_SHAPES.some((shape) => shape.url === draftAvatarKey);
  const initialsSelected = !draftAvatarKey || (!isPresetSelected && !isUploadedPhoto(draftAvatarKey));

  const cardTitleStyle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: "0.93rem",
    color: "rgba(255,255,255,0.88)",
  };

  return (
    <div style={{ maxWidth: isMobile ? "100%" : 720, width: "100%", display: "grid", gap: "1.25rem", minWidth: 0, boxSizing: "border-box" }}>
      {/* ── Profile ── */}
      <section style={{ ...sectionStyle, display: "grid", gap: isMobile ? "1rem" : "1.5rem" }}>
        <div style={cardTitleStyle}>Profile</div>

        {/* Display name */}
        <div style={{ display: "grid", gap: "0.45rem" }}>
          <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", fontWeight: 600 }}>
            Display name
          </label>
          <input
            type="text"
            value={draftName}
            maxLength={40}
            disabled={disableWrites}
            placeholder="Display name"
            onChange={(e) => setDraftName(e.target.value)}
            style={{
              width: "100%",
              padding: "0.62rem 0.85rem",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
              color: "white",
              outline: "none",
              fontSize: "0.92rem",
              opacity: disableWrites ? 0.6 : 1,
              boxSizing: "border-box",
            }}
          />
          {!isMobile && (
            <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", lineHeight: 1.4 }}>
              Shown in the header. Defaults to your email name.
            </div>
          )}
        </div>

        {/* Account icon */}
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <div style={{ display: "grid", gap: "0.35rem" }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", fontWeight: 600 }}>Account icon</div>
            {!isMobile && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", marginTop: "0.2rem" }}>Shown in the header and menus.</div>}
          </div>

          {/* Preview + compact actions */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "0.8rem" : "1.25rem", flexWrap: "wrap" }}>
            {/* Non-interactive preview — same component as real header, no chevron */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                color: "rgba(255,255,255,0.25)",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                lineHeight: 1,
              }}>
                Preview
              </span>
              <div style={{ pointerEvents: "none", userSelect: "none", opacity: 0.9 }}>
                <AccountBadge
                  avatarKey={draftAvatarKey}
                  initials={previewInitials}
                  displayName={previewName}
                  syncStatus={resolvedSyncStatus}
                  isAuthenticated={isSignedIn}
                  showChevron={false}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              <ModeTab
                label="Preset"
                active={avatarChooserMode === "preset"}
                onClick={() => toggleChooserMode("preset")}
                disabled={disableWrites}
              />
              <ModeTab
                label="Upload"
                active={avatarChooserMode === "upload"}
                onClick={() => toggleChooserMode("upload")}
                disabled={disableWrites}
              />
            </div>
          </div>

          {/* Preset mode: shape grid */}
          {avatarChooserMode === "preset" && (
            <div style={{ display: "grid", gap: "0.5rem" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
              <button
                type="button"
                disabled={disableWrites}
                onClick={() => setDraftAvatarKey("")}
                title="Initials"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  border: initialsSelected ? "2px solid rgba(59,130,246,0.9)" : "1px solid rgba(255,255,255,0.12)",
                  background: initialsSelected ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.03)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: initialsSelected ? "#e0f2fe" : "rgba(255,255,255,0.8)",
                  fontSize: "1rem",
                  fontWeight: 800,
                  cursor: disableWrites ? "not-allowed" : "pointer",
                }}
              >
                {defaultInitial}
              </button>
              {PRESET_SHAPES.map((shape) => {
                const selected = draftAvatarKey === shape.url;
                return (
                  <button
                    key={shape.id}
                    type="button"
                    disabled={disableWrites}
                    onClick={() => setDraftAvatarKey(shape.url)}
                    title={shape.label}
                    style={{
                      width: 44, height: 44, borderRadius: 10, padding: 9,
                      border: selected ? "2px solid rgba(59,130,246,0.9)" : "1px solid rgba(255,255,255,0.12)",
                      background: selected ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.03)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      cursor: disableWrites ? "not-allowed" : "pointer",
                    }}
                  >
                    <img src={shape.url} alt={shape.label} style={{ width: 22, height: 22, opacity: selected ? 1 : 0.65 }} />
                  </button>
                );
              })}
              </div>
            </div>
          )}

          {/* Image mode: upload */}
          {avatarChooserMode === "upload" && (
            <div style={{ display: "grid", gap: "0.45rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap" }}>
              <label
                style={{
                  ...btn,
                  background: "rgba(59,130,246,0.12)",
                  borderColor: "rgba(59,130,246,0.3)",
                  color: "#bfdbfe",
                  cursor: disableWrites ? "not-allowed" : "pointer",
                  opacity: disableWrites ? 0.6 : 1,
                }}
                title={disableWrites ? "Disabled in Demo" : "Upload a custom image"}
              >
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={disableWrites}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    if (f.size > 512 * 1024) {
                      setSaveMsg("Image too large — use a file under 512 KB.");
                      return;
                    }
                    handleAvatarUpload(f);
                  }}
                />
              </label>
              {isUploadedPhoto(draftAvatarKey) && (
                <img
                  src={draftAvatarKey}
                  alt="preview"
                  style={{ width: 36, height: 36, borderRadius: 999, objectFit: "cover", border: "1px solid rgba(255,255,255,0.18)" }}
                />
              )}
              {!isUploadedPhoto(draftAvatarKey) && (
                <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem" }}>No image uploaded yet.</span>
              )}
              </div>
            </div>
          )}
        </div>

        {/* Save changes (only when dirty) */}
        {isDirty && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <button
              style={{
                ...btn,
                background: actionColors.primaryGradient,
                borderColor: "rgba(148,163,184,0.45)",
                color: actionColors.primaryText,
              }}
              onClick={() => { void handleSave(); }}
              disabled={disableWrites || saveBusy}
              title={disableWrites ? "Disabled in Demo" : ""}
            >
              {saveBusy ? "Saving…" : "Save changes"}
            </button>
            {saveMsg && (
              <span style={{ fontSize: "0.83rem", color: saveMsg === "Saved!" ? "#86efac" : "#fca5a5" }}>
                {saveMsg}
              </span>
            )}
          </div>
        )}
        {/* Show save message even when not dirty (e.g. error after clearing dirty state) */}
        {!isDirty && saveMsg && (
          <span style={{ fontSize: "0.83rem", color: saveMsg === "Saved!" ? "#86efac" : "#fca5a5" }}>
            {saveMsg}
          </span>
        )}
      </section>

    </div>
  );
}
